import { createContext, useState } from 'react'

import useProfileFieldsValidation from './profileFieldsValidation'
import { USER_INIT } from 'constants/initialStates'

import { useHomeStore } from 'context/HomeStore'
import { withAuthStore } from 'context/AuthStore'

import { useResErrors, useRoutes, useStateCallback } from 'hooks'

import { AuthService, NotificationService, SalesService } from 'lib/api'

export const ProfileStoreContext = createContext({
  profileStore: {},
  profileStoreFunc: {}
})

const ProfileStoreProvider = props => {
  const [loading, setLoading] = useState(false)
  const [loadingInit, setLoadingInit] = useState(false)
  const [user, setUser] = useStateCallback(USER_INIT)
  const [dashboardList, setDashboardList] = useState([])
  const [reportList] = useState([])
  const [superset, setSuperset] = useState({
    accessList: [],
    roleList: []
  })
  const { standardCheck } = useResErrors()
  const { goPrevPage } = useRoutes()
  const {
    homeStore: { dictionary }
  } = useHomeStore()

  const onChangeUser = (key, value) =>
    setUser(prevState => ({ ...prevState, [key]: value }))

  const getDashboardList = async () =>
    await AuthService.getDashboardReports().then(res =>
      standardCheck(res, () => setDashboardList(res?.data || []))
    )

  const getInitDataEditUser = async userId => {
    setLoadingInit(true)

    AuthService.getUserById(userId).then(response =>
      standardCheck(
        response,
        async () => {
          const data = response.data?.[0]
          const user = {
            ...data,
            managersProjectId: data?.managersProjectId,
            headManagerUserId: data?.headManagerUser?.id || null,
            ropUserId: data?.ropUser?.id || null,
            branchCode: data?.branch?.code || null,
            phone: data?.phone?.toPhone()
          }

          setUser(user)
          await getDashboardList()
          setLoadingInit(false)
        },
        {
          textErr: 'Ошибка при получении данных пользователя',
          prevFailAction: () => setLoadingInit(false),
          onFail: () => goPrevPage()
        }
      )
    )
  }

  const uploadAvatar = async file => {
    setLoading(true)
    await AuthService.uploadFile(file).then(res =>
      standardCheck(
        res,
        () => {
          onChangeUser('photo', res?.data?.fileDownloadUri)
          setLoading(false)
        },
        {
          textErr: 'Ошибка при загрузке аватара',
          prevFailAction: () => setLoading(false)
        }
      )
    )
  }

  const addNewRole = role => {
    setUser(prevState => ({
      ...prevState,
      roles: [...prevState?.roles, role]
    }))
  }

  const removeRole = (id, isIndex = false) => {
    setUser(prevState => ({
      ...prevState,
      roles: isIndex
        ? prevState?.roles.filter((_, index) => index !== id)
        : prevState?.roles.filter(item => item?.id !== id)
    }))
  }

  const handleDeleteRole = async id => {
    if (!!user?.id) {
      setLoading(true)
      return await AuthService.deleteUserRole({
        userId: user?.id,
        roleId: id
      }).then(res =>
        standardCheck(
          res,
          async () => {
            removeRole(id)
            setLoading(false)
          },
          {
            prevFailAction: () => setLoading(false),
            textErr: 'Ошибка при удалении роли'
          }
        )
      )
    }

    removeRole(id)
  }

  const setRolesFromRoleList = roles => {
    if (user?.roles?.length > 0) {
      const newRoles = roles.filter(
        role =>
          !user?.roles?.find(
            item => item?.project?.code === role?.project?.code
          )
      )
      return setUser(prevState => ({
        ...prevState,
        roles: [...prevState?.roles, ...newRoles]
      }))
    }
    setUser(prevState => ({
      ...prevState,
      roles
    }))
  }

  const {
    errors: profileFieldsErrors,
    validate: profileFieldsValidate,
    resetError: profileFieldsResetError
  } = useProfileFieldsValidation(
    user,
    !!user?.roles?.find(e => e?.project?.code === 'BX')
  )

  const onSave = () =>
    profileFieldsValidate(!!user?.id ? updateUser : createUser)

  const createUser = async () => {
    setLoading(true)
    await AuthService.createUser(user).then(res =>
      standardCheck(
        res,
        () => {
          setLoading(false)
          goPrevPage()
        },
        {
          textErr:
            res.status === 500
              ? 'По этому ИИН уже существует основная учетная запись'
              : 'Ошибка при сохранении пользователя',
          prevFailAction: () => setLoading(false)
        }
      )
    )
  }

  const updateUser = async () => {
    setLoading(true)
    await AuthService.updateUser(user).then(res =>
      standardCheck(
        res,
        async () => {
          const saleSubdivision =
            user?.subdivision &&
            dictionary.subdivisions.find(
              s => s.projectId === 18 && s.name === user.subdivision
            )

          if (saleSubdivision) {
            const data = {
              assignTo: user.username,
              subdivision: user.subdivision
            }
            await SalesService.updateSalesSubdivision(data)
            await NotificationService.updateEventSubdivision(data)
          }

          setLoading(false)
          goPrevPage()
        },
        {
          textErr: 'Ошибка при сохранении пользователя',
          prevFailAction: () => setLoading(false)
        }
      )
    )
  }

  const profileStore = {
    user,
    dashboardList,
    reportList,
    loading,
    superset,
    loadingInit,
    validation: {
      profileFieldsErrors
    }
  }

  const profileStoreFunc = {
    onChangeUser,
    getInitDataEditUser,
    uploadAvatar,
    addNewRole,
    removeRole,
    handleDeleteRole,
    setRolesFromRoleList,
    validation: {
      profileFieldsResetError
    },
    onSave
  }

  const { children } = props
  return (
    <ProfileStoreContext.Provider value={{ profileStore, profileStoreFunc }}>
      {children}
    </ProfileStoreContext.Provider>
  )
}

export default withAuthStore(ProfileStoreProvider)
